import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

/* ─────────────────────────────────────────────────────────────
   Types
───────────────────────────────────────────────────────────── */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  duration: number;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

/* ─────────────────────────────────────────────────────────────
   Context
───────────────────────────────────────────────────────────── */
const ToastContext = createContext<ToastContextValue | null>(null);

/* ─────────────────────────────────────────────────────────────
   Style helpers
───────────────────────────────────────────────────────────── */
const TOAST_STYLES: Record<ToastType, { bg: string; border: string; icon: string; color: string }> = {
  success: { bg: '#f0fdf4', border: '#16a34a', icon: '✅', color: '#166534' },
  error:   { bg: '#fef2f2', border: '#dc2626', icon: '❌', color: '#991b1b' },
  warning: { bg: '#fffbeb', border: '#d97706', icon: '⚠️', color: '#92400e' },
  info:    { bg: '#eff6ff', border: '#2563eb', icon: 'ℹ️', color: '#1e40af' },
};

/* ─────────────────────────────────────────────────────────────
   Single Toast item
───────────────────────────────────────────────────────────── */
const ToastEntry: React.FC<{ toast: ToastItem; onRemove: (id: number) => void }> = ({ toast, onRemove }) => {
  const style = TOAST_STYLES[toast.type];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderLeft: `4px solid ${style.border}`,
        borderRadius: '10px',
        padding: '12px 16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        minWidth: '280px',
        maxWidth: '400px',
        animation: 'toastSlideIn 0.25s ease',
        position: 'relative',
      }}
    >
      <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '1px' }}>{style.icon}</span>
      <span style={{ fontSize: '13px', fontWeight: 500, color: style.color, flex: 1, lineHeight: '1.5' }}>
        {toast.message}
      </span>
      <button
        onClick={() => onRemove(toast.id)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#9ca3af', fontSize: '16px', padding: '0',
          flexShrink: 0, lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Provider
───────────────────────────────────────────────────────────── */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counterRef = useRef(0);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration = 4000) => {
    const id = ++counterRef.current;
    setToasts(prev => [...prev, { id, type, message, duration }]);
    setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(30px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      {children}
      {/* Container */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      >
        {toasts.map(toast => (
          <div key={toast.id} style={{ pointerEvents: 'all' }}>
            <ToastEntry toast={toast} onRemove={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

/* ─────────────────────────────────────────────────────────────
   Hook
───────────────────────────────────────────────────────────── */
export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
};
